import React, { useEffect, useRef } from 'react';
import 'swagger-ui-dist/swagger-ui.css';
import { FaPlug, FaExternalLinkAlt, FaTerminal, FaBookOpen } from 'react-icons/fa';
import { useWorkspace } from '../../context/WorkspaceContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.walytic.com';
const N8N_PACKAGE_URL = 'https://www.npmjs.com/package/n8n-nodes-walytic';

export default function SwaggerView({ scope, showIntegrations = true } = {}) {
  const containerRef = useRef(null);
  const { activeWorkspace } = useWorkspace() || {};
  const workspaceId = activeWorkspace?.clientId || activeWorkspace?.id || localStorage.getItem('activeWorkspaceId') || '';

  useEffect(() => {
    let mounted = true;
    let ui = null;

    // Lazy-load swagger-ui-dist so it isn't part of the main bundle
    import('swagger-ui-dist/swagger-ui-bundle.js').then((mod) => {
      if (!mounted) return;
      const SwaggerUIBundle = mod.default || mod;
      ui = SwaggerUIBundle({
        // scope=admin returns the full spec (admin/db/external ops included);
        // the default (public) spec strips every Admin:-tagged operation.
        url: `${API_BASE}/api/openapi.json${scope === 'admin' ? '?scope=admin' : ''}`,
        domNode: containerRef.current,
        deepLinking: true,
        docExpansion: 'list',
        defaultModelsExpandDepth: 0,
        filter: true,
        tryItOutEnabled: true,
        requestInterceptor: (req) => {
          // Browser cookies (httpOnly token) flow with credentials.
          req.credentials = 'include';
          req.headers = req.headers || {};
          if (workspaceId) {
            req.headers['X-Workspace-Id'] = workspaceId;
            req.headers['X-Client-Id'] = workspaceId;
          }
          return req;
        },
        persistAuthorization: true,
      });
    });

    return () => {
      mounted = false;
      // swagger-ui mounts directly into the DOM node; clearing it on unmount
      // prevents duplicate renders on hot reload.
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
  }, [scope, workspaceId]);

  const copyInstall = () => {
    navigator.clipboard?.writeText('n8n-nodes-walytic');
  };

  return (
    <div className="h-full overflow-y-auto bg-surface-bg">
      {showIntegrations && (
      /* n8n integration card */
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <div className="rounded-xl border border-surface-border bg-surface-card overflow-hidden">
          <div className="flex flex-col md:flex-row gap-4 p-5 md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 text-orange-500 flex items-center justify-center flex-shrink-0">
                <FaPlug size={18} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-ink-primary">
                  Use Walytic in n8n
                </h3>
                <p className="text-xs text-ink-muted mt-1 max-w-xl leading-relaxed">
                  Drop the official community node into your n8n workflow to send WhatsApp
                  messages, manage contacts, and trigger campaigns without writing API code.
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
                  <a
                    href={N8N_PACKAGE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-brand hover:text-brand-700 font-medium"
                  >
                    <FaExternalLinkAlt size={9} /> n8n-nodes-walytic on npm
                  </a>
                  <span className="text-ink-faint">·</span>
                  <a
                    href="https://docs.n8n.io/integrations/community-nodes/installation/"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-brand hover:text-brand-700 font-medium"
                  >
                    <FaBookOpen size={10} /> n8n install guide
                  </a>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-stretch md:items-end gap-2">
              <button
                type="button"
                onClick={copyInstall}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-surface-border bg-surface-bg hover:border-brand/30 transition-colors text-xs font-mono text-ink-primary"
                title="Copy package name"
              >
                <FaTerminal size={10} className="text-ink-faint" />
                n8n-nodes-walytic
              </button>
              <span className="text-2xs text-ink-faint">
                Settings → Community Nodes → Install
              </span>
            </div>
          </div>

          <div className="border-t border-surface-border bg-surface-bg/50 px-5 py-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div>
                <p className="font-medium text-ink-primary mb-0.5">Credential</p>
                <p className="text-ink-muted leading-relaxed">
                  Walytic API key (from <span className="font-mono">Settings → API Keys</span>) —
                  sent as <span className="font-mono">x-api-key</span>.
                </p>
              </div>
              <div>
                <p className="font-medium text-ink-primary mb-0.5">Operations</p>
                <p className="text-ink-muted leading-relaxed">
                  Send Message · Send Group · Verify Number · Create/Update Contact · Trigger
                  Campaign · Webhook trigger for incoming events.
                </p>
              </div>
              <div>
                <p className="font-medium text-ink-primary mb-0.5">Self-hosted n8n</p>
                <p className="text-ink-muted leading-relaxed">
                  Set <span className="font-mono">N8N_COMMUNITY_PACKAGES_ENABLED=true</span> in
                  your n8n env, then install via the UI or
                  <span className="font-mono"> npm i n8n-nodes-walytic</span> in your custom
                  Docker image.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
        <div className="rounded-xl border border-surface-border bg-surface-card p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-ink-primary">Active workspace for API requests</p>
            <p className="text-xs text-ink-muted mt-1">
              Try-It requests on this page automatically send <span className="font-mono">X-Workspace-Id</span> and <span className="font-mono">X-Client-Id</span>.
            </p>
          </div>
          <div className="min-w-0 md:text-right">
            <p className="text-xs text-ink-muted truncate">{activeWorkspace?.name || 'Selected workspace'}</p>
            <code className="block text-xs font-mono text-ink-secondary truncate">
              {workspaceId || 'Workspace loading...'}
            </code>
          </div>
        </div>
      </div>

      {/* Swagger UI */}
      <div className="max-w-6xl mx-auto px-2 sm:px-6 py-6">
        <div className="rounded-xl border border-surface-border bg-white overflow-hidden">
          <div ref={containerRef} className="swagger-ui-host" />
        </div>
      </div>
    </div>
  );
}
